/**
 * Phase 18 — customer-side Web Push helpers.
 *
 * One small module that encapsulates all the browser-API ceremony
 * around Web Push so the page components (`Cards.tsx`,
 * `ProfileNotifications.tsx`) stay readable.
 *
 * Public surface:
 *   • isPushSupported()              — fast capability check
 *   • isIOSSafariNonStandalone()     — iOS-specific gate (Apple only
 *                                      delivers push to home-screen
 *                                      installed PWAs; Safari tab
 *                                      sessions can't subscribe)
 *   • requestPermissionAndSubscribe()— full happy-path flow
 *   • unsubscribe()                  — local + server soft-delete
 *   • urlBase64ToUint8Array()        — VAPID public key decoder (the
 *                                      browser PushManager.subscribe
 *                                      API only accepts a Uint8Array)
 *
 * All server calls go through `customerFetch` so the bearer token,
 * envelope decoding, and error shape are consistent with the rest of
 * the customer client.
 */
import { customerFetch } from "./customerFetch";

/** True iff the runtime supports the Web Push + Notifications stack. */
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * iOS Safari only delivers Web Push to home-screen-installed PWAs
 * (added via the share sheet → "Add to Home Screen"). Subscribing
 * from a regular Safari tab silently no-ops, so we surface a guided
 * install prompt instead of letting the user smash the button to
 * no effect. The `standalone` flag is the iOS-only signal that the
 * PWA was launched from the home-screen icon.
 */
export function isIOSSafariNonStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  if (!isIOS) return false;
  // `navigator.standalone` is iOS-Safari-specific and not in the
  // standard TS DOM lib.
  const nav = navigator as unknown as { standalone?: boolean };
  return nav.standalone !== true;
}

/**
 * Decode the VAPID server public key (base64url, URL-safe, no
 * padding) into a Uint8Array. The Web Push API insists on bytes,
 * not a string.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export interface SubscribeResult {
  ok: boolean;
  /** Present on success. */
  subscriptionId?: string;
  /** Present on failure. */
  errorCode?:
    | "NOT_SUPPORTED"
    | "IOS_NEEDS_INSTALL"
    | "PERMISSION_DENIED"
    | "PERMISSION_DEFAULT"
    | "VAPID_FETCH_FAILED"
    | "PUSH_SUBSCRIBE_FAILED"
    | "SERVER_REJECT"
    | "ALREADY_SUBSCRIBED_LOCALLY";
  errorMessage?: string;
}

/**
 * The end-to-end subscribe flow:
 *   1. Capability + iOS-install gates.
 *   2. Pull the active SW registration (vite-plugin-pwa registers it
 *      on app startup; if it isn't ready yet we await it).
 *   3. Ask the user for permission. If denied or default-with-no-
 *      action, return the appropriate code so the caller can show
 *      the right help copy.
 *   4. Fetch the VAPID public key from the server.
 *   5. Call `pushManager.subscribe({applicationServerKey})`.
 *   6. POST the resulting subscription JSON to the server, which
 *      upserts (idempotent) and enforces the 5-active-per-customer
 *      cap.
 */
export async function requestPermissionAndSubscribe(): Promise<SubscribeResult> {
  if (!isPushSupported()) {
    return { ok: false, errorCode: "NOT_SUPPORTED" };
  }
  if (isIOSSafariNonStandalone()) {
    return { ok: false, errorCode: "IOS_NEEDS_INSTALL" };
  }

  const reg = await navigator.serviceWorker.ready;

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission === "denied") {
    return { ok: false, errorCode: "PERMISSION_DENIED" };
  }
  if (permission !== "granted") {
    return { ok: false, errorCode: "PERMISSION_DEFAULT" };
  }

  const vapidRes = await customerFetch<{ publicKey: string }>(
    "/api/v1/customer/me/push/vapid-public-key",
  );
  if (!vapidRes.ok) {
    return {
      ok: false,
      errorCode: "VAPID_FETCH_FAILED",
      errorMessage: vapidRes.message,
    };
  }

  let subscription: PushSubscription;
  try {
    // If a stale subscription is sitting on the SW (e.g. user
    // previously revoked server-side but the browser kept the
    // local handle), reuse it — the server upsert is idempotent
    // and will resurrect the row.
    const existing = await reg.pushManager.getSubscription();
    subscription =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // PushManager.subscribe accepts a BufferSource here. Recent
        // TS DOM libs added an `ArrayBufferLike` generic to Uint8Array
        // that confuses the overload resolution — the runtime types
        // are fine, just the static check needs the explicit cast.
        applicationServerKey: urlBase64ToUint8Array(
          vapidRes.data.publicKey,
        ) as BufferSource,
      }));
  } catch (err) {
    return {
      ok: false,
      errorCode: "PUSH_SUBSCRIBE_FAILED",
      errorMessage: err instanceof Error ? err.message : String(err),
    };
  }

  const json = subscription.toJSON();
  // The browser `PushSubscription.toJSON()` shape on Chromium/Firefox
  // is `{endpoint, keys: {p256dh, auth}, expirationTime}`; we lift
  // the keys out to the flat shape the server expects.
  const keys = json.keys as { p256dh?: string; auth?: string } | undefined;
  if (!json.endpoint || !keys?.p256dh || !keys?.auth) {
    return {
      ok: false,
      errorCode: "PUSH_SUBSCRIBE_FAILED",
      errorMessage: "Browser returned an incomplete subscription.",
    };
  }

  const subRes = await customerFetch<{ id: string }>(
    "/api/v1/customer/me/push/subscriptions",
    {
      method: "POST",
      body: JSON.stringify({
        endpoint: json.endpoint,
        p256dhKey: keys.p256dh,
        authSecret: keys.auth,
        userAgent: navigator.userAgent.slice(0, 300),
      }),
    },
  );
  if (!subRes.ok) {
    return {
      ok: false,
      errorCode: "SERVER_REJECT",
      errorMessage: subRes.message,
    };
  }

  return { ok: true, subscriptionId: subRes.data.id };
}

/**
 * Local + server soft-delete. We always ask the browser to drop the
 * push subscription FIRST (so a server-side error can't leave the
 * user in an "I clicked unsubscribe but still get pushes" state),
 * then DELETE the server row.
 */
export async function unsubscribe(): Promise<{ ok: boolean; message?: string }> {
  if (!isPushSupported()) return { ok: true };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };

  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (err) {
    return {
      ok: false,
      message: err instanceof Error ? err.message : String(err),
    };
  }

  // Find the server row matching the endpoint we just dropped, then
  // DELETE it. We use the list endpoint rather than threading the
  // server id through localStorage so that a freshly-installed
  // browser (or a cleared cache) can still cleanly revoke.
  const listRes = await customerFetch<{
    subscriptions: Array<{ id: string; endpoint: string }>;
  }>("/api/v1/customer/me/push/subscriptions");
  if (!listRes.ok) {
    // The browser-side unsubscribe already happened; the server row
    // will be cleaned up by the dispatcher's GONE handler the next
    // time it tries to push. Not a hard failure.
    return { ok: true };
  }
  const match = listRes.data.subscriptions.find((s) => s.endpoint === endpoint);
  if (!match) return { ok: true };

  const delRes = await customerFetch(
    `/api/v1/customer/me/push/subscriptions/${match.id}`,
    { method: "DELETE" },
  );
  if (!delRes.ok) {
    return { ok: false, message: delRes.message };
  }
  return { ok: true };
}
